import React from 'react';
import { useWatch } from 'react-hook-form';
import { InputField, CheckboxField } from 'components/form/rhf';
import { useCountries, useRegions } from 'modules/geo';
import { Control } from 'react-hook-form/dist/types/form';
import { Divider, Stack, Typography } from '@mui/material';
import { LocationOnOutlined, AccountCircleOutlined, LocalShippingOutlined } from '@mui/icons-material';

import { AddressFields } from './AddressFields';
import { CustomerFormValues } from './customerFormSchema';

interface Props {
  control: Control<CustomerFormValues>;
}

export const CustomerAccountFormFields = ({ control }: Props) => {
  const billingCountry = useWatch({ control, name: 'billingAddress.country' });
  const deliveryCountry = useWatch({ control, name: 'deliveryAddress.country' });
  const sameAsBilling = useWatch({ control, name: 'sameAsBilling' });

  const { countries } = useCountries();
  const { regions: billingRegions } = useRegions(billingCountry);
  const { regions: deliveryRegions } = useRegions(deliveryCountry);

  return (
    <Stack gap="1rem">
      <Stack gap="0.75rem">
        <Stack direction="row" gap="0.5rem" alignItems="center">
          <AccountCircleOutlined />

          <Typography variant="subtitle2" component="span">
            Customer Information
          </Typography>
        </Stack>

        <Stack direction="row" gap="1rem">
          <InputField
            name="name"
            label="Name"
            control={control}
            inputProps={{
              required: true,
              variant: 'outlined',
            }}
          />
        </Stack>

        <Stack direction="row" gap="1rem">
          <InputField
            name="phone"
            label="Phone"
            control={control}
            inputProps={{
              type: 'tel',
              required: true,
              variant: 'outlined',
              mask: '(999) 999-9999',
            }}
          />

          <InputField
            name="email"
            label="Email"
            control={control}
            inputProps={{
              variant: 'outlined',
            }}
          />
        </Stack>
      </Stack>

      <Divider />

      <AddressFields
        control={control}
        prefix="billingAddress"
        title="Billing Address"
        icon={<LocationOnOutlined />}
        countries={countries}
        regions={billingRegions}
        country={billingCountry}
      />

      <Divider />

      <Stack gap="0.75rem">
        <Stack direction="row" gap="0.75rem" alignItems="center" justifyContent="space-between">
          <Stack direction="row" gap="0.5rem" alignItems="center">
            <LocalShippingOutlined />

            <Typography variant="subtitle2" component="span">
              Delivery Address
            </Typography>
          </Stack>

          <CheckboxField control={control} name="sameAsBilling" label="Same as Billing Address" />
        </Stack>

        {!sameAsBilling && (
          <AddressFields
            control={control}
            prefix="deliveryAddress"
            countries={countries}
            regions={deliveryRegions}
            country={deliveryCountry}
          />
        )}
      </Stack>
    </Stack>
  );
};
